import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import scale from "../../assets/images/scale.webp";
import { aibeSubjectDefinitions } from "../../data/aibeModuleData";

export function BareActsGallery({ subjects }) {
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const normalizeText = (value = "") =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const levenshteinDistance = (a, b) => {
    const matrix = Array.from(
      { length: b.length + 1 },
      (_, row) => Array(a.length + 1).fill(0)
    );

    for (let i = 0; i <= b.length; i++) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        matrix[i][j] =
          b[i - 1] === a[j - 1]
            ? matrix[i - 1][j - 1]
            : Math.min(
              matrix[i - 1][j] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j - 1] + 1
            );
      }
    }

    return matrix[b.length][a.length];
  };

  const getSearchScore = (subjectName, query) => {
    const name = normalizeText(subjectName);
    const search = normalizeText(query);

    if (!search) return 0;

    if (name === search) return 1000;
    if (name.includes(search)) return 900;

    const searchWords = search.split(" ");
    const nameWords = name.split(" ");

    let score = 0;

    searchWords.forEach((searchWord) => {
      if (!searchWord) return;

      if (nameWords.some((word) => word.startsWith(searchWord))) {
        score += 700;
        return;
      }

      if (nameWords.some((word) => word.includes(searchWord))) {
        score += 600;
        return;
      }

      let bestDistance = Infinity;

      nameWords.forEach((word) => {
        const distance = levenshteinDistance(searchWord, word);
        bestDistance = Math.min(bestDistance, distance);
      });

      const maxDistance =
        searchWord.length <= 4
          ? 1
          : searchWord.length <= 7
            ? 2
            : 3;

      if (bestDistance <= maxDistance) {
        score += 400 - bestDistance * 50;
      }
    });

    return score;
  };

  const orderedSubjects = useMemo(() => {
    const query = normalizeText(searchTerm);

    if (!query) {
      return showAll ? subjects : subjects.slice(0, 12);
    }

    const scoredSubjects = subjects.map((subject, index) => ({
      subject,
      index,
      score: getSearchScore(subject.name, query),
    }));

    const matched = scoredSubjects
      .filter((item) => item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.index - b.index;
      });

    const unmatched = scoredSubjects
      .filter((item) => item.score === 0)
      .sort((a, b) => a.index - b.index);

    return [...matched, ...unmatched].map((item) => item.subject);
  }, [subjects, searchTerm, showAll]);

  const visibleSubjects = searchTerm
    ? orderedSubjects
    : showAll
      ? subjects
      : subjects.slice(0, 12);

  return (
    <section
      className="aibe-bareacts-gallery"
      aria-label="AIBE Bare Acts Gallery"
    >
      <h2 className="aibe-section-heading">
        AIBE BARE ACTS GALLERY
      </h2>

      <div className="aibe-gallery-search-wrapper">
        <input
          type="search"
          className="aibe-gallery-search-input"
          placeholder="Search Bare Acts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search AIBE Bare Acts"
        />

        {searchTerm && (
          <button
            type="button"
            className="aibe-gallery-search-clear"
            onClick={() => setSearchTerm("")}
            aria-label="Clear Bare Act search"
          >
            ×
          </button>
        )}
      </div>

      <div className="aibe-bareacts-row">
        {visibleSubjects.map((subject) => (
          <a
            key={subject.id}
            className="AIBEbook-container"
            href={subject.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${subject.name} Bare Act in a new tab`}
          >
            <div className="AIBEbook-spine"></div>

            <div className="AIBEbook-cover">
              <div className="AIBEbook-title-rect">
                <img
                  src={scale}
                  alt="TejCodex"
                  className="AIBEbook-icon"
                />

                <div className="AIBEbook-title">
                  {subject.name}
                </div>

                <div className="AIBEbook-comment">
                  OPEN BARE ACT
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {searchTerm && (
        <p className="aibe-search-result-info">
          Search results are shown first. Other Bare Acts remain listed below.
        </p>
      )}

      {!searchTerm && subjects.length > 12 && (
        <div className="aibe-gallery-show-all">
          <button
            type="button"
            className="aibe-show-all-btn"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? "SHOW LESS" : "SHOW ALL SUBJECTS"}
          </button>
        </div>
      )}
    </section>
  );
}
// Section 3: Understanding Exam Pattern & Structure
export function ExamStructureSection() {
  const points = [
    {
      emoji: "📝",
      title: "100 MCQs Format",
      value: "100 Questions",
      text: "Covers 19 legal subjects ranging from Constitutional Law to Minor Acts. All questions are objective multiple-choice questions.",
    },
    {
      emoji: "⏱️",
      title: "Exam Duration",
      value: "3.5 Hours (210 Mins)",
      text: "Generous timeframe allowing approximately 2.1 minutes per question, perfect for looking up section indices.",
    },
    {
      emoji: "🎯",
      title: "Marking Scheme",
      value: "No Negative Marking",
      text: "Earn +1 mark for every correct answer. Zero penalty for wrong or unattempted answers — attempt all 100 questions!",
    },
    {
      emoji: "⚖️",
      title: "Passing Marks",
      value: "45% Gen/OBC • 40% SC/ST",
      text: "Qualifying exam, not a competitive rank race. Gen/OBC require 45 marks; SC/ST/PwD require 40 marks.",
    },
    {
      emoji: "📖",
      title: "Open Book / Bare Acts",
      value: "Bare Acts Permitted",
      text: "Carry clean, unannotated Bare Acts to the exam hall per Bar Council of India guidelines for fast index searching.",
    },
    {
      emoji: "🎓",
      title: "Core Purpose",
      value: "Certificate of Practice",
      text: "Passing AIBE grants your permanent Certificate of Practice (COP) to advocate in any court across India.",
    },
  ];

  return (
    <section className="aibe-section-block">
      <h2 className="aibe-block-heading">Understanding the Exam Pattern & Structure</h2>
      <p className="aibe-block-subheading">
        Detailed breakdown of the All India Bar Examination mechanics and rules...
      </p>

      <div className="aibe-grid-3col">
        {points.map((pt, idx) => (
          <div key={idx} className="aibe-pattern-card">
            <div className="aibe-pattern-card-header">
              <span className="aibe-card-emoji">{pt.emoji}</span>
              <img src={scale} alt="TejCodex Logo" className="aibe-card-logo" />
            </div>
            <h3>{pt.title}</h3>
            <div className="aibe-card-value-badge">{pt.value}</div>
            <p>{pt.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Section 4: Prioritise Syllabus & High Yield Subjects
export function PrioritySyllabusSection() {
  const [activeTier, setActiveTier] = useState("high");
  const priorityData = [
    {
      id: "high",
      tier: "High Priority",
      meta: "8 - 10 Questions per Subject",
      color: "high",
      badgeEmoji: "🔴",
      description: "Crucial core subjects that account for over 60% of total marks in AIBE.",
      subjects: [
        { name: "Constitutional Law", qCount: "10 Questions", topics: "Fundamental Rights, Writs, DPSP, Judiciary" },
        { name: "CrPC / BNSS (2023)", qCount: "10 Questions", topics: "FIR, Bail, Trial Procedure, Remand" },
        { name: "CPC (Civil Procedure Code)", qCount: "10 Questions", topics: "Jurisdiction, Res Judicata, Pleadings, Decrees" },
        { name: "Evidence Act / BSA (2023)", qCount: "8 Questions", topics: "Relevancy, Admissions, Burden of Proof, Dying Decl." },
        { name: "Law of Contract & Specific Relief", qCount: "8 Questions", topics: "Offer & Acceptance, Damages, Performance" },
        { name: "Family Law", qCount: "8 Questions", topics: "Hindu Marriage, Succession, Muslim Law, Adoption" },
        { name: "IPC / BNS (2023)", qCount: "8 Questions", topics: "General Exceptions, Offences Against Body & Prop" },
      ],
    },
    {
      id: "medium",
      tier: "Medium Priority",
      meta: "4 - 5 Questions per Subject",
      color: "medium",
      badgeEmoji: "🟡",
      description: "High return-on-investment subjects with concise, easy-to-navigate Bare Acts.",
      subjects: [
        { name: "Professional Ethics & BCI Rules", qCount: "4 Questions", topics: "Advocates Act 1961, Misconduct, BCI Rules" },
        { name: "Law of Torts & MV Act", qCount: "5 Questions", topics: "Negligence, Strict Liability, Consumer Claims" },
        { name: "Arbitration & Conciliation Act", qCount: "4 Questions", topics: "Arbitration Agreement, Awards, Conciliation" },
        { name: "Public International Law & HR", qCount: "5 Questions", topics: "UN Charter, Human Rights Conventions" },
      ],
    },
    {
      id: "low",
      tier: "Low Priority",
      meta: "2 - 3 Questions per Subject",
      color: "low",
      badgeEmoji: "🟢",
      description: "Minor statutory acts to be attempted using index lookup during final pass.",
      subjects: [
        { name: "Administrative Law", qCount: "3 Questions", topics: "Delegated Legislation, Natural Justice" },
        { name: "Cyber Law / IT Act", qCount: "2 Questions", topics: "Digital Signatures, Cyber Offences" },
        { name: "Environmental Law", qCount: "2 Questions", topics: "EP Act 1986, NGT Act, Pollution Rules" },
        { name: "Company Law", qCount: "2 Questions", topics: "Incorporation, Directors, Winding Up" },
        { name: "Labour & Industrial Law", qCount: "3 Questions", topics: "Industrial Disputes, Factories Act" },
        { name: "Taxation, IPR & Land Laws", qCount: "2 Qs Each", topics: "Basic Definitions, Schedule Lookup" },
      ],
    },
  ];
  const activeGroup = priorityData.find((group) => group.id === activeTier) || priorityData[0];

  return (
    <section className="aibe-section-block">
      <h2 className="aibe-block-heading">Practice Syllabus & High Yield Subjects</h2>
      <p className="aibe-block-subheading">
        Strategic classification of subjects into High, Medium, and Low priority tiers...
      </p>

      <div className="aibe-priority-split">
        <div className="aibe-priority-rail" aria-label="Priority tiers">
          {priorityData.map((group) => (
            <button
              type="button"
              key={group.id}
              className={`aibe-priority-block tier-${group.color} ${activeTier === group.id ? "active" : ""}`}
              onClick={() => setActiveTier(group.id)}
            >
              <span className="aibe-priority-badge">{group.badgeEmoji}</span>
              <strong>{group.tier}</strong>
              <small>{group.meta}</small>
            </button>
          ))}
        </div>

        <div className={`aibe-priority-detail-panel tier-${activeGroup.color}`}>
          <div className="aibe-priority-group-header">
            <div className="aibe-priority-title-wrap">
              <span className="aibe-priority-badge">{activeGroup.badgeEmoji}</span>
              <div>
                <h3>{activeGroup.tier}</h3>
                <p>{activeGroup.meta}</p>
              </div>
            </div>
            <img src={scale} alt="TejCodex Logo" className="aibe-priority-logo" />
          </div>
          <p className="aibe-priority-desc">{activeGroup.description}</p>
          <div className="aibe-subject-cards-grid">
            {activeGroup.subjects.map((sub, sIdx) => (
              <div key={sIdx} className="aibe-subject-mini-card">
                <div className="aibe-sub-card-top">
                  <h4>{sub.name}</h4>
                  <span className="aibe-qcount-tag">{sub.qCount}</span>
                </div>
                <p className="aibe-sub-card-topics">
                  <strong>Key Topics:</strong> {sub.topics}
                </p>
                <div className="aibe-sub-card-footer">
                  <img src={scale} alt="TejCodex" className="aibe-mini-logo" />
                  <span>TejCodex Verified</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Section 5: Examination Tactics Details
export function TacticsSection() {
  const tactics = [
    {
      step: "01",
      emoji: "📘",
      title: "Carry Clean Bare Acts Only",
      detail:
        "Carry unannotated Bare Acts without written notes or commentary to prevent last-minute center objections. Ensure recent editions with up-to-date amendments.",
    },
    {
      step: "02",
      emoji: "🔍",
      title: "Master Index-First Search",
      detail:
        "Read the question, locate the exact legal subject, flip directly to the Bare Act index, locate the Chapter heading, then jump immediately to the target Section.",
    },
    {
      step: "03",
      emoji: "⏱️",
      title: "Pacing & Time Division",
      detail:
        "Allocate 60 mins for Round 1 (Direct Answers), 90 mins for Round 2 (Bare Act Index Lookup), and 30 mins for Round 3 (Elimination & Final Review).",
    },
    {
      step: "04",
      emoji: "⚖️",
      title: "Old vs New Laws Readiness",
      detail:
        "Keep both legacy statutes (IPC, CrPC, Evidence) and 2023 Sanhitas (BNS, BNSS, BSA) ready depending on your exam hall ticket guidelines.",
    },
    {
      step: "05",
      emoji: "🖊️",
      title: "OMR Bubble Precision",
      detail:
        "Use dark black/blue ballpoint pens. Bubble answers every 10–15 questions rather than waiting until the end to avoid bubbling errors.",
    },
    {
      step: "06",
      emoji: "🎯",
      title: "Attempt All 100 Questions",
      detail:
        "Since AIBE has zero negative marking, never leave a single question unattempted! Use logical elimination for unknown questions.",
    },
  ];

  return (
    <section className="aibe-section-block">
      <h2 className="aibe-block-heading">Examination Tactics Details</h2>
      <p className="aibe-block-subheading">
        Proven Bare Act lookup techniques and exam execution tactics...
      </p>

      <div className="aibe-grid-2col">
        {tactics.map((t, idx) => (
          <div key={idx} className="aibe-tactic-card">
            <div className="aibe-tactic-header">
              <span className="aibe-tactic-step">STEP {t.step}</span>
              <span className="aibe-tactic-emoji">{t.emoji}</span>
              <img src={scale} alt="TejCodex Logo" className="aibe-tactic-logo" />
            </div>
            <h3>{t.title}</h3>
            <p>{t.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Section 6: Traffic Light Strategy for AIBE
export function TrafficLightStrategy() {
  const [activeLight, setActiveLight] = useState("green");
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveLight((prev) => {
        if (prev === "green") return "yellow";
        if (prev === "yellow") return "red";
        return "green";
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const lightData = [
    {
      id: "green",
      name: "Green Light",
      label: "Round 1: Known Immediately",
      timeText: "0 - 10 sec per Q",
      icon: "🟢",
      tagline: "High Confidence • Instant Recall",
      description:
        "Questions on fundamental legal principles, landmark definitions, or constitutional provisions that you know immediately without needing a Bare Act.",
      strategy:
        "Mark these immediately in Round 1. Do NOT waste time searching Bare Acts for questions you know with 100% certainty. Secure these 40–50 easy marks first to build exam momentum.",
      example: "Example: Article 14 (Equality Before Law) or Article 21 (Protection of Life & Personal Liberty).",
    },
    {
      id: "yellow",
      name: "Yellow Light",
      label: "Round 2: Bare Act Index Lookup",
      timeText: "1 - 2 min per Q",
      icon: "🟡",
      tagline: "Index Search • Section & Rule Verification",
      description:
        "Questions specifying exact Section numbers, Order & Rules (CPC), or procedural steps where you know the subject but need quick provision verification.",
      strategy:
        "Flag for Round 2. Use the Index-First Strategy: Open the Bare Act index, locate the Chapter heading, jump directly to the specific provision, and verify option accuracy.",
      example: "Example: CPC Order 39 Rule 1 (Temporary Injunctions) or CrPC Section 154 / BNSS Section 173 (FIR).",
    },
    {
      id: "red",
      name: "Red Light",
      label: "Round 3: Unknown / Minor Statutes",
      timeText: "Final 30 Minutes",
      icon: "🔴",
      tagline: "Elimination Technique • Zero Negative Marking",
      description:
        "Unfamiliar minor acts, obscure case law questions, or complex problem scenarios that require deep searching.",
      strategy:
        "Do NOT panic and do NOT waste time early in the paper. Leave these for the final round. Use logical elimination of options. Since AIBE has ZERO negative marking, attempt EVERY red question before submission!",
      example: "Example: Obscure clauses in Cyber Law, Specific Relief Act schedules, or Arbitration fee rules.",
    },
  ];

  const currentData = lightData.find((item) => item.id === activeLight);

  return (
    <section className="aibe-section-block traffic-light-section" id="traffic-light-section">
      <h2 className="aibe-block-heading">Traffic Light Strategy for AIBE</h2>
      <p className="aibe-block-subheading">
        {isPaused ? "⏸️ Paused — Click any light to inspect strategy manually" : "🔄 Auto-cycling every 3 seconds — Hover to pause strategy view"}
      </p>

      <div
        className="traffic-strategy-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Realistic Traffic Signal Casing */}
        <div className="traffic-signal-casing">
          <div className="traffic-signal-top-cap"></div>

          {/* RED LIGHT */}
          <div
            className={`traffic-light-housing ${activeLight === "red" ? "active-housing" : ""}`}
            onClick={() => {
              setActiveLight("red");
              setIsPaused(true);
            }}
          >
            <div className="traffic-light-hood"></div>
            <div className={`traffic-light-bulb light-red ${activeLight === "red" ? "active-glow" : ""}`}>
              <span className="light-inner-glow"></span>
            </div>
            <span className="traffic-light-label">RED</span>
          </div>

          {/* YELLOW LIGHT */}
          <div
            className={`traffic-light-housing ${activeLight === "yellow" ? "active-housing" : ""}`}
            onClick={() => {
              setActiveLight("yellow");
              setIsPaused(true);
            }}
          >
            <div className="traffic-light-hood"></div>
            <div className={`traffic-light-bulb light-yellow ${activeLight === "yellow" ? "active-glow" : ""}`}>
              <span className="light-inner-glow"></span>
            </div>
            <span className="traffic-light-label">YELLOW</span>
          </div>

          {/* GREEN LIGHT */}
          <div
            className={`traffic-light-housing ${activeLight === "green" ? "active-housing" : ""}`}
            onClick={() => {
              setActiveLight("green");
              setIsPaused(true);
            }}
          >
            <div className="traffic-light-hood"></div>
            <div className={`traffic-light-bulb light-green ${activeLight === "green" ? "active-glow" : ""}`}>
              <span className="light-inner-glow"></span>
            </div>
            <span className="traffic-light-label">GREEN</span>
          </div>

          <div className="traffic-signal-base-post"></div>
        </div>

        {/* Synced Content Cards Panel */}
        <div className="traffic-cards-panel">
          <div className="traffic-cards-tabs">
            {lightData.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`traffic-tab-btn tab-${item.id} ${activeLight === item.id ? "tab-active" : ""}`}
                onClick={() => {
                  setActiveLight(item.id);
                  setIsPaused(true);
                }}
              >
                <span className="tab-icon">{item.icon}</span>
                <span className="tab-name">{item.name}</span>
              </button>
            ))}
          </div>

          {/* Active Card Body */}
          <div className={`traffic-card-body card-${activeLight}`}>
            <div className="traffic-card-header">
              <div className="traffic-card-title-group">
                <span className="traffic-card-badge">{currentData.tagline}</span>
                <h3>{currentData.label}</h3>
              </div>
              <span className="traffic-time-badge">⏱️ {currentData.timeText}</span>
            </div>

            <div className="traffic-card-content">
              <p className="traffic-card-desc">
                <strong>What it is:</strong> {currentData.description}
              </p>

              <div className="traffic-card-strategy-box">
                <h4>🎯 Strategy & Action Plan:</h4>
                <p>{currentData.strategy}</p>
              </div>

              <div className="traffic-card-example-box">
                💡 <em>{currentData.example}</em>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Section 7: Exam Day Checklist (No checkbox boxes, pure clean detail grid list with emojis & scale logo)
export function ExamChecklistGrid() {
  const checklistItems = [
    {
      emoji: "📄",
      title: "2 Printed Admit Card Copies",
      detail: "Print two clear copies of your AIBE Hall Ticket with clearly visible candidate photograph and exam center code.",
    },
    {
      emoji: "🆔",
      title: "Original Photo ID Proof",
      detail: "Carry original Aadhaar Card, Voter ID, Passport, or Bar Council State Enrollment Card for hall verification.",
    },
    {
      emoji: "🖊️",
      title: "Black & Blue Ballpoint Pens",
      detail: "Bring multiple smooth black and blue ballpoint pens specifically designed for darkening OMR answer sheet bubbles.",
    },
    {
      emoji: "📚",
      title: "Clean Bare Acts Collection",
      detail: "Carry unannotated Bare Acts without detailed commentary or written notes as permitted by Bar Council rules.",
    },
    {
      emoji: "⌚",
      title: "Analog Wrist Watch",
      detail: "Wear a simple analog wrist watch to monitor your 3.5-hour exam pacing without relying on wall clocks.",
    },
    {
      emoji: "🏫",
      title: "Exam Center Arrival",
      detail: "Reach the exam venue by 11:30 AM (at least 1.5 hours prior to exam start) to complete gate security & seating checks.",
    },
  ];

  return (
    <section className="aibe-section-block">
      <h2 className="aibe-block-heading">Exam Day Checklist</h2>
      <p className="aibe-block-subheading">
        Essential documents and instructions for a smooth exam day experience...
      </p>

      <div className="aibe-grid-3col">
        {checklistItems.map((item, idx) => (
          <div key={idx} className="aibe-checklist-detail-card">
            <div className="aibe-checklist-card-top">
              <span className="aibe-checklist-emoji">{item.emoji}</span>
              <img src={scale} alt="TejCodex Logo" className="aibe-checklist-logo" />
            </div>
            <h3>{item.title}</h3>
            <p>{item.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PracticeCards({ onOpen }) {
  const modes = [
    {
      id: "paper",
      title: "Previous Paper Wise",
      text: "Practice questions from previous AIBE question papers.",
    },
    {
      id: "random",
      title: "Random Practice",
      text: "Practice a mixed set of questions from all available subjects and papers.",
    },
  ];

  return (
    <div className="aibe-practice-cards">
      {modes.map((mode) => (
        <button
          key={mode.id}
          type="button"
          onClick={() => onOpen(mode.id)}
        >
          <img
            src={scale}
            alt="TejCodex Logo"
            className="aibe-practice-card-logo"
          />

          <strong>{mode.title}</strong>

          <span>{mode.text}</span>
        </button>
      ))}
    </div>
  );
}


function shuffle(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

function getAnswerState(question, selectedOption, submitted) {
  if (!submitted) return {};
  return question.options.reduce((state, option) => {
    if (option.id === question.correctOptionId) state[option.id] = "correct";
    if (option.id === selectedOption && option.id !== question.correctOptionId) {
      state[option.id] = "incorrect";
    }
    return state;
  }, {});
}

export function DifficultySelector({ value, onChange }) {
  const options = [
    { id: "easy", label: "Easy", seconds: 100 },
    { id: "medium", label: "Medium", seconds: 80 },
    { id: "hard", label: "Hard", seconds: 40 },
  ];
  return (
    <div className="aibe-difficulty" role="group" aria-label="Difficulty">
      {options.map((option) => (
        <button
          type="button"
          key={option.id}
          className={value === option.id ? "active" : ""}
          onClick={() => onChange(option)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function QuestionTimer({ seconds, onExpire, isRunning = true, resetKey = 0 }) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);
  }, [seconds, resetKey]);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }

    if (remaining <= 0) {
      onExpire();
      return undefined;
    }
    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [isRunning, remaining, onExpire]);

  return (
    <div className="aibe-timer">
      <span>⏱️ Timer: {remaining}s</span>
    </div>
  );
}

export function PerformanceAnalytics({ answers }) {
  const categoryMap = {
    "administrative-law": "Administrative Law",
    "adr": "Arbitration & Conciliation",
    "arbitration": "Arbitration & Conciliation",
    "bns": "IPC / BNS",
    "bnss": "CrPC / BNSS",
    "bsa": "Evidence Act / BSA",
    "company-law": "Company Law",
    "constitution": "Constitutional Law",
    "consumer-law": "Consumer Protection",
    "contracts": "Law of Contract & Specific Relief",
    "cpc": "CPC",
    "crpc": "CrPC / BNSS",
    "environment": "Environmental Law",
    "evidence": "Evidence Act / BSA",
    "family-law": "Family Law",
    "intellectual-property": "Intellectual Property Rights",
    "ipc": "IPC / BNS",
    "jurispedence": "Jurisprudence",
    "jurisprudence": "Jurisprudence",
    "jurispudence": "Jurisprudence",
    "labour-law": "Labour & Industrial Law",
    "limitation": "Limitation",
    "motor-vehicles": "Law of Torts & Motor Vehicles",
    "negotiable-instruments": "Negotiable Instruments",
    "professional-ethics": "Professional Ethics & BCI",
    "property-law": "Property Law",
    "public-international-law": "Public International Law",
    "specific-relief": "Law of Contract & Specific Relief",
    "taxation": "Taxation",
    "torts": "Law of Torts & Motor Vehicles",
    "transfer-of-property": "Transfer of Property"
  };

  const byScope = answers.reduce((result, answer) => {
    let key = answer.subject || answer.year || "General";

    // Map to beautiful group names using the map first, or fallback to the injected group
    if (categoryMap[key]) {
      key = categoryMap[key];
    } else {
      const subjectDef = aibeSubjectDefinitions.find((s) => s.key === key);
      if (subjectDef && subjectDef.group) {
        key = subjectDef.group;
      }
    }

    if (!result[key]) {
      result[key] = {
        total: 0,
        correct: 0,
      };
    }

    result[key].total += 1;

    if (answer.correct) {
      result[key].correct += 1;
    }

    return result;
  }, {});

  const totalAttempted = answers.length;

  const totalCorrect = answers.filter(
    (answer) => answer.correct
  ).length;

  const totalIncorrect =
    totalAttempted - totalCorrect;


  const subjects = Object.entries(byScope)
    .map(([nameKey, data]) => {
      const percent =
        data.total > 0
          ? Math.round(
            (data.correct / data.total) * 100
          )
          : 0;

      return {
        name: nameKey,
        percent,
        total: data.total,
        correct: data.correct,
      };
    })
    .sort((a, b) => a.percent - b.percent);

  const needsWork = subjects
    .filter((subject) => subject.percent < 45)
    .slice(0, 5);

  const strengths = subjects
    .filter((subject) => subject.percent >= 75)
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 5);

  if (!answers.length) {
    return (
      <div className="aibe-analysis">
        <div className="aibe-analysis-empty">
          Complete some questions to view your performance.
        </div>
      </div>
    );
  }

  return (
    <div className="aibe-analysis">

      {/* SUBJECT INSIGHTS */}
      <div className="aibe-performance-insights">

        {/* NEEDS WORK */}
        <div className="aibe-performance-card">

          <h3>
            🔴 NEEDS WORK
          </h3>

          {needsWork.length > 0 ? (
            needsWork.map((subject) => (
              <div
                key={subject.name}
                className="aibe-performance-subject"
              >
                <span>{subject.name}</span>

                <strong>
                  {subject.percent}%
                </strong>
              </div>
            ))
          ) : (
            <p>
              No subjects need urgent revision.
            </p>
          )}

        </div>


        {/* STRENGTH */}
        <div className="aibe-performance-card">

          <h3>
            🟢 YOUR STRENGTH
          </h3>

          {strengths.length > 0 ? (
            strengths.map((subject) => (
              <div
                key={subject.name}
                className="aibe-performance-subject"
              >
                <span>{subject.name}</span>

                <strong>
                  {subject.percent}%
                </strong>
              </div>
            ))
          ) : (
            <p>
              Keep practicing to build strong subjects.
            </p>
          )}

        </div>

      </div>

    </div>
  );
}
export function PracticeSelectionGrid({
  items,
  type,
  onSelect,
}) {
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const normalizeText = (value = "") =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const levenshteinDistance = (a, b) => {
    const matrix = Array.from(
      { length: b.length + 1 },
      (_, row) => Array(a.length + 1).fill(0)
    );

    for (let i = 0; i <= b.length; i++) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        matrix[i][j] =
          b[i - 1] === a[j - 1]
            ? matrix[i - 1][j - 1]
            : Math.min(
              matrix[i - 1][j] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j - 1] + 1
            );
      }
    }

    return matrix[b.length][a.length];
  };

  const getSearchScore = (itemName, query) => {
    const name = normalizeText(itemName);
    const search = normalizeText(query);

    if (!search) return 0;

    if (name === search) return 1000;
    if (name.includes(search)) return 900;

    const searchWords = search.split(" ");
    const nameWords = name.split(" ");

    let score = 0;

    searchWords.forEach((searchWord) => {
      if (!searchWord) return;

      if (nameWords.some((word) => word.startsWith(searchWord))) {
        score += 700;
        return;
      }

      if (nameWords.some((word) => word.includes(searchWord))) {
        score += 600;
        return;
      }

      let bestDistance = Infinity;

      nameWords.forEach((word) => {
        const distance = levenshteinDistance(searchWord, word);
        bestDistance = Math.min(bestDistance, distance);
      });

      const maxDistance =
        searchWord.length <= 4
          ? 1
          : searchWord.length <= 7
            ? 2
            : 3;

      if (bestDistance <= maxDistance) {
        score += 400 - bestDistance * 50;
      }
    });

    return score;
  };

  const orderedItems = useMemo(() => {
    const query = normalizeText(searchTerm);

    if (!query) {
      return showAll ? items : items.slice(0, 12);
    }

    const scoredItems = items.map((item, index) => ({
      item,
      index,
      score: getSearchScore(item.name, query),
    }));

    const matched = scoredItems
      .filter((entry) => entry.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.index - b.index;
      });

    const unmatched = scoredItems
      .filter((entry) => entry.score === 0)
      .sort((a, b) => a.index - b.index);

    return [...matched, ...unmatched].map((entry) => entry.item);
  }, [items, searchTerm, showAll]);

  const visibleItems = searchTerm
    ? orderedItems
    : showAll
      ? items
      : items.slice(0, 12);

  const searchPlaceholder =
    type === "subject"
      ? "Search subjects..."
      : "Search previous papers...";

  return (
    <>
      <div className="aibe-practice-search-wrapper">
        <input
          type="search"
          className="aibe-practice-search-input"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}

        />

        {searchTerm && (
          <button
            type="button"
            className="aibe-practice-search-clear"
            onClick={() => setSearchTerm("")}
          >
            ×
          </button>
        )}
      </div>

      <div className="aibe-practice-selection-grid">
        {visibleItems.map((item) => (
          <button
            type="button"
            key={item.id}
            className="aibe-practice-selection-card"
            onClick={() => onSelect(item)}
          >
            <img
              src={scale}
              alt="TejCodex Logo"
              className="aibe-practice-selection-logo"
            />

            <strong>
              {item.name}
            </strong>

            <span>
              {type === "subject"
                ? "SUBJECT WISE"
                : "PREVIOUS PAPER"}
            </span>
          </button>
        ))}
      </div>

      {searchTerm && (
        <p className="aibe-search-result-info">
          Matching results are shown first. Other {type === "subject" ? "subjects" : "papers"} remain listed below.
        </p>
      )}

      {!searchTerm && items.length > 12 && (
        <div className="aibe-practice-show-all">
          <button
            type="button"
            className="aibe-show-all-btn"
            onClick={() => setShowAll((value) => !value)}
          >
            {showAll
              ? "SHOW LESS"
              : type === "subject"
                ? "SHOW ALL SUBJECTS"
                : "SHOW ALL PAPERS"}
          </button>
        </div>
      )}
    </>
  );
}
export function SummaryPage({ answers, total, onBack, onAgain, onPracticePage }) {
  const correct = answers.filter((answer) => answer.correct).length;
  const incorrect = answers.filter(
    (answer) => Boolean(answer.selectedOptionId) && !answer.correct
  ).length;
  const unattempted = Math.max(
    0,
    total - correct - incorrect
  );
  const accuracy = total
    ? Math.round((correct / total) * 100)
    : 0;

  return (
    <section className="aibe-summary">
      <h2>Practice Session Summary</h2>
      <div className="aibe-summary-grid">
        <div className="aibe-card">
          <span>Accuracy</span>
          <strong>{accuracy}%</strong>
        </div>
        <div className="aibe-card">
          <span>Correct Answers</span>
          <strong>{correct}</strong>
        </div>
        <div className="aibe-card">
          <span>Incorrect Answers</span>
          <strong>{incorrect}</strong>
        </div>
        <div className="aibe-card">
          <span>Unattempted</span>
          <strong>{unattempted}</strong>
        </div>
        <div className="aibe-card">
          <span>Score</span>
          <strong>{correct}/{total}</strong>
        </div>
      </div>
      <h3>Performance Breakdown</h3>
      <PerformanceAnalytics answers={answers} />
      <div className="aibe-action-row">
        <button type="button" className="aibe-btn-secondary" onClick={onBack}>Practice Again</button>
        <button type="button" className="aibe-btn-primary" onClick={onAgain}>Go Back</button>
        <button type="button" className="aibe-btn-secondary" onClick={onPracticePage}>Practice Page</button>
      </div>
    </section>
  )
}

export function QuestionInterface({
  questions,
  initialIndex = 0,
  onExit,
  mode,
  onQuestionChange,
  onQuestionNavigate,
}) {
  const [index, setIndex] = useState(initialIndex);

  // Stores the selected option for EVERY question.
  // This prevents previous answers from disappearing when navigating.
  // Stores the student's selected option for EVERY question.
  const [selectedAnswers, setSelectedAnswers] = useState({});

  // Stores which questions have had their correct answer revealed.
  const [revealedAnswers, setRevealedAnswers] = useState({});

  const [selectedOption, setSelectedOption] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [expired, setExpired] = useState(false);
  const [postExpireCountdown, setPostExpireCountdown] = useState(null);
  const [timerResetKey, setTimerResetKey] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [explanationsOpen, setExplanationsOpen] = useState({});
  const [difficulty, setDifficulty] = useState({
    id: "medium",
    seconds: 80,
  });

  // Controls the final submission tab.
  const [showSubmitPrompt, setShowSubmitPrompt] = useState(false);
  const [submitPromptType, setSubmitPromptType] = useState("manual");
  useEffect(() => {
    // Completely new practice session.
    setSelectedAnswers({});
    setRevealedAnswers({});
    setSelectedOption("");
    setSubmitted(false);
    setAnswerRevealed(false);
    setShowExplanation(false);
    setExpired(false);
    setPostExpireCountdown(null);
    setAnswers([]);
    setExplanationsOpen({});
    setShowSubmitPrompt(false);
    setSubmitPromptType("manual");
    setTimerResetKey((value) => value + 1);
  }, [questions]);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);
  const question = questions[index];
  useEffect(() => {
    if (!question) return;

    const savedOption = selectedAnswers[question.id] || "";
    const wasRevealed = Boolean(revealedAnswers[question.id]);

    // Restore the student's previous selection.
    setSelectedOption(savedOption);

    // Revealed state is completely independent of selection.
    setAnswerRevealed(wasRevealed);

    // Only a revealed answer is considered submitted/checked.
    setSubmitted(wasRevealed);

    // Restore normal review state when navigating.
    setExpired(false);
    setPostExpireCountdown(null);
    setShowExplanation(Boolean(explanationsOpen[question.id]));
  }, [explanationsOpen, question, selectedAnswers, revealedAnswers]);

  useEffect(() => {
    if (question && onQuestionChange) {
      onQuestionChange(question, index);
    }
  }, [index, onQuestionChange, question]);

  const answerState = question ? getAnswerState(question, selectedOption, answerRevealed) : {};

  const recordAnswer = useCallback(
    (chosenOptionId = selectedOption) => {
      if (!question) return;

      // Always remember the selected option.
      setSelectedAnswers((current) => ({
        ...current,
        [question.id]: chosenOptionId,
      }));

      // Keep the answer collection synchronized.
      setAnswers((current) => {
        const filtered = current.filter(
          (answer) => answer.questionId !== question.id
        );

        return [
          ...filtered,
          {
            questionId: question.id,
            subject: question.subject,
            year: question.year,
            selectedOptionId: chosenOptionId,
            correct:
              Boolean(chosenOptionId) &&
              chosenOptionId === question.correctOptionId,
          },
        ];
      });
    },
    [question, selectedOption]
  );
  const resetQuestionState = useCallback(() => {
    if (!question) return;

    // Retry means deliberately clearing the student's answer
    // for this question.
    setSelectedAnswers((current) => {
      const next = { ...current };
      delete next[question.id];
      return next;
    });

    setRevealedAnswers((current) => {
      const next = { ...current };
      delete next[question.id];
      return next;
    });

    setExplanationsOpen((current) => {
      const next = { ...current };
      delete next[question.id];
      return next;
    });

    setSelectedOption("");
    setSubmitted(false);
    setAnswerRevealed(false);
    setShowExplanation(false);
    setExpired(false);
    setPostExpireCountdown(null);
    setTimerResetKey((value) => value + 1);
  }, [question]);

  const moveToQuestion = useCallback(
    (nextIndex, options = {}) => {
      if (nextIndex < 0 || nextIndex >= questions.length || nextIndex === index) return;
      setIndex(nextIndex);
      if (options.recordHistory !== false && onQuestionNavigate) {
        onQuestionNavigate(nextIndex);
      }
    },
    [index, onQuestionNavigate, questions.length]
  );

  const nextQuestion = useCallback((options = {}) => {
    if (index >= questions.length - 1) return;
    moveToQuestion(index + 1, options);
  }, [index, moveToQuestion, questions.length]);

  const previousQuestion = useCallback(() => {
    if (index <= 0) return;
    moveToQuestion(index - 1);
  }, [index, moveToQuestion]);

  const retry = () => resetQuestionState();

  const checkAnswer = () => {
    if (!selectedOption || answerRevealed) return;

    // Remember the selected option.
    setSelectedAnswers((current) => ({
      ...current,
      [question.id]: selectedOption,
    }));

    // Mark this question as checked/revealed.
    setRevealedAnswers((current) => ({
      ...current,
      [question.id]: true,
    }));

    // Keep the existing answer collection updated.
    setAnswers((current) => {
      const filtered = current.filter(
        (answer) => answer.questionId !== question.id
      );

      return [
        ...filtered,
        {
          questionId: question.id,
          subject: question.subject,
          year: question.year,
          selectedOptionId: selectedOption,
          correct: selectedOption === question.correctOptionId,
        },
      ];
    });

    setSubmitted(true);
    setAnswerRevealed(true);
    setExpired(false);
    setPostExpireCountdown(null);
    setShowExplanation(false);
    setExplanationsOpen((current) => ({
      ...current,
      [question.id]: false,
    }));

    // Final question opens the session submission prompt.
    if (index === questions.length - 1) {
      setShowSubmitPrompt(true);
      setSubmitPromptType("manual");
    }
  };
  const handleTimerExpire = useCallback(() => {
    if (answerRevealed) return;

    // Preserve the selected answer, even if the student
    // did not press Check Answer.
    const currentSelectedOption =
      selectedAnswers[question.id] || selectedOption || "";

    if (currentSelectedOption) {
      setSelectedAnswers((current) => ({
        ...current,
        [question.id]: currentSelectedOption,
      }));

      setAnswers((current) => {
        const filtered = current.filter(
          (answer) => answer.questionId !== question.id
        );

        return [
          ...filtered,
          {
            questionId: question.id,
            subject: question.subject,
            year: question.year,
            selectedOptionId: currentSelectedOption,
            correct:
              currentSelectedOption === question.correctOptionId,
          },
        ];
      });
    }

    // Reveal the correct answer.
    setRevealedAnswers((current) => ({
      ...current,
      [question.id]: true,
    }));

    setSubmitted(true);
    setAnswerRevealed(true);
    setExpired(true);

    // Start the 10-second review period.
    setPostExpireCountdown(10);
  }, [
    answerRevealed,
    question,
    selectedAnswers,
    selectedOption,
  ]);
  const addReviewTime = useCallback(() => {
    if (!expired || postExpireCountdown === null) return;

    // Add exactly 10 seconds to the current review period.
    setPostExpireCountdown((current) => {
      if (current === null) return 10;
      return current + 10;
    });
  }, [expired, postExpireCountdown]);
  const handleDifficultyChange = (nextDifficulty) => {
    setDifficulty(nextDifficulty);
    resetQuestionState();
  };

  const toggleExplanation = () => {
    if (!question || !answerRevealed) return;

    setShowExplanation((value) => {
      const nextValue = !value;
      setExplanationsOpen((current) => ({
        ...current,
        [question.id]: nextValue,
      }));
      return nextValue;
    });
  };

  useEffect(() => {
    if (!expired || postExpireCountdown === null) {
      return undefined;
    }

    if (postExpireCountdown <= 0) {
      if (index < questions.length - 1) {
        // Automatically move to the next question after review without
        // creating duplicate browser history.
        nextQuestion({ recordHistory: false });
      } else {
        // Final question: open submission prompt.
        setPostExpireCountdown(null);
        setShowSubmitPrompt(true);
        setSubmitPromptType("timeout");
      }

      return undefined;
    }

    const timer = setTimeout(() => {
      setPostExpireCountdown((value) =>
        value === null ? null : value - 1
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    expired,
    index,
    nextQuestion,
    postExpireCountdown,
    questions.length,
  ]);
  if (!questions.length || !question) {
    return <p className="aibe-empty-state">Please select a subject or yearly paper to proceed.</p>;
  }
  const handleSubmitSession = () => {
    // Build the FINAL answer set directly from every question
    // and the student's saved selections.
    //
    // This guarantees that answers remain counted even when
    // the student navigates between questions without pressing
    // Check Answer.

    const finalAnswers = questions.map((currentQuestion) => {
      const selectedOptionId =
        selectedAnswers[currentQuestion.id] || "";

      return {
        questionId: currentQuestion.id,
        subject: currentQuestion.subject,
        year: currentQuestion.year,
        selectedOptionId,
        correct:
          Boolean(selectedOptionId) &&
          selectedOptionId === currentQuestion.correctOptionId,
      };
    });

    onExit(finalAnswers);

    setShowSubmitPrompt(false);
  };

  const handleReviewSession = () => {
    // Simply close the submission prompt.
    // The current question/session remains untouched.
    setShowSubmitPrompt(false);

    // For timeout on the final question, keep the final question visible.
    setPostExpireCountdown(null);
  };
  return (

    <section className="aibe-question-panel">
      {showSubmitPrompt && (
        <div
          className="aibe-session-submit-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aibe-session-submit-title"
        >
          <div className="aibe-session-submit-tab">
            <h3 id="aibe-session-submit-title">
              {submitPromptType === "timeout"
                ? "Time Up!"
                : "Submit the Session"}
            </h3>

            <p>
              {submitPromptType === "timeout"
                ? "Time Up! Submit the Session to check your score or review back your answers."
                : "Submit the Session to check your score."}
            </p>

            <div className="aibe-action-row">
              <button
                type="button"
                className="aibe-btn-secondary"
                onClick={handleReviewSession}
              >
                Review
              </button>

              <button
                type="button"
                className="aibe-btn-primary"
                onClick={handleSubmitSession}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="aibe-question-topbar">
        <QuestionTimer
          seconds={difficulty.seconds}
          onExpire={handleTimerExpire}
          isRunning={!answerRevealed}
          resetKey={`${question?.id || "empty"}-${timerResetKey}`}
        />
        <DifficultySelector value={difficulty.id} onChange={handleDifficultyChange} />
        <button
          type="button"
          className="aibe-session-close"
          onClick={() => {
            setShowSubmitPrompt(true);
            setSubmitPromptType("manual");
          }}
          aria-label="Submit practice session"
        >
          X
        </button>
      </div>
      <p className="aibe-question-count">
        {mode} - Question {index + 1} of {questions.length}
      </p>
      <h3 className="aibe-question-text">{question.question}</h3>
      {answerRevealed && (
        <p className="aibe-reference">
          {question.bareActName} | {question.reference} | {question.difficulty}
        </p>
      )}
      <div className="aibe-options" role="radiogroup">
        {question.options.map((option) => (
          <label key={option.id} className={`aibe-option-label ${answerState[option.id] || ""}`}>
            <input
              type="radio"
              name={question.id}
              checked={selectedOption === option.id}
              disabled={answerRevealed}
              onChange={() => {
                if (answerRevealed) return;

                setSelectedOption(option.id);

                // Immediately persist the selection.
                setSelectedAnswers((current) => ({
                  ...current,
                  [question.id]: option.id,
                }));
              }}
            />
            <span>{option.text}</span>
          </label>
        ))}
      </div>
      {answerRevealed && (
        <div className="aibe-explanation">
          <strong>Correct Answer: {question.correctOptionId.toUpperCase()}</strong>
          {expired && (
            <p>
              Time's up - correct answer revealed.
              {postExpireCountdown !== null
                ? index < questions.length - 1
                  ? ` Next question in ${postExpireCountdown} seconds.`
                  : ` Final question review: ${postExpireCountdown} seconds.`
                : ""}
            </p>
          )}
        </div>
      )}
      {showExplanation && answerRevealed && (
        <div className="aibe-explanation">
          <p>{question.explanation}</p>
        </div>
      )}
      <div className="aibe-practice-action-bar">
        <button type="button" className="aibe-icon-nav" onClick={previousQuestion} disabled={index === 0} aria-label="Previous question">&lt;</button>
        <button type="button" className="aibe-btn-secondary" onClick={toggleExplanation} disabled={!answerRevealed}>Explanation</button>
        {expired && postExpireCountdown !== null ? (
          <button
            type="button"
            className="aibe-btn-primary"
            onClick={addReviewTime}
          >
            Add Time (+10s)
          </button>
        ) : (
          <button
            type="button"
            className="aibe-btn-primary"
            onClick={checkAnswer}
            disabled={!selectedOption || answerRevealed}
          >
            Check Answer
          </button>
        )}
        <button type="button" className="aibe-btn-secondary" onClick={retry}>Retry</button>
        <button type="button" className="aibe-icon-nav" onClick={nextQuestion} disabled={index === questions.length - 1} aria-label="Next question">&gt;</button>
      </div>
    </section>
  );
}

const isValidPracticeQuestion = (question) =>
  Boolean(
    question &&
    question.id &&
    question.question &&
    Array.isArray(question.options) &&
    question.correctOptionId
  );

export function PracticeCentre({ subjects, papers, subjectQuestions, paperQuestions, aibeNavigation }) {
  const [mode, setMode] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedPaper, setSelectedPaper] = useState("");
  const [sessionStarted, setSessionStarted] = useState(false);
  const [summaryAnswers, setSummaryAnswers] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const appliedNavigationStateRef = useRef("");


  const allPaperQuestions = useMemo(
    () => Object.values(paperQuestions).flat().filter(isValidPracticeQuestion),
    [paperQuestions],
  );

  const subjectItems = useMemo(
    () => subjects.map((subject) => ({
      id: String(subject.id),
      key: subject.key,
      name: subject.name,
      questionKey: subject.questionKey,
      bareActKey: subject.bareActKey,
    })),
    [subjects],
  );

  const paperItems = useMemo(
    () =>
      [...papers]
        .sort((a, b) => {
          const aNumber = parseInt(a.id.replace("aibe", ""), 10);
          const bNumber = parseInt(b.id.replace("aibe", ""), 10);

          return bNumber - aNumber;
        })
        .map((paper) => ({
          id: paper.id,
          name: paper.name,
          year: paper.year,
          datasetKey: paper.datasetKey || paper.id,
        })),
    [papers],
  );

  const selectedSubjectItem = subjectItems.find((subject) => subject.id === selectedSubject);
  const selectedPaperItem = paperItems.find((paper) => paper.id === selectedPaper);

  const questions = useMemo(() => {

    if (mode === "paper" && selectedPaperItem) {
      return (paperQuestions[selectedPaperItem.datasetKey] || []).filter(isValidPracticeQuestion);
    }
    if (mode === "random") {
      return shuffle([...allPaperQuestions]).slice(0, 100);
    }
    return [];
  }, [allPaperQuestions, mode, paperQuestions, selectedPaperItem]);

  const activeQuestionIndex = questions.length
    ? Math.min(Math.max(questionIndex, 0), questions.length - 1)
    : 0;

  const practiceModeLabel =
    mode === "paper"
      ? "Previous Paper Wise"
      : "Random Practice";

  const buildPracticeState = useCallback(
    (overrides = {}) => ({
      screen: "practice",
      view: "setup",
      mode,
      subjectId: selectedSubject,
      paperId: selectedPaper,
      questionIndex,
      ...overrides,
    }),
    [mode, questionIndex, selectedPaper, selectedSubject]
  );

  useEffect(() => {
    const navState = aibeNavigation?.currentAibeState;
    if (!navState || navState.screen !== "practice") return;

    const serialized = JSON.stringify(navState);
    if (appliedNavigationStateRef.current === serialized) return;
    appliedNavigationStateRef.current = serialized;

    const nextMode = navState.mode || "";
    const nextView = navState.view || "setup";

    setMode(nextMode);
    setSelectedSubject(navState.subjectId || "");
    setSelectedPaper(navState.paperId || "");
    setQuestionIndex(Number.isFinite(navState.questionIndex) ? navState.questionIndex : 0);
    setSessionStarted(nextView === "session" || nextView === "summary");

    if (nextView !== "summary") {
      setSummaryAnswers(null);
    }
  }, [aibeNavigation?.currentAibeState]);

  const resetSession = () => {
    setMode("");
    setSelectedSubject("");
    setSelectedPaper("");
    setSessionStarted(false);
    setSummaryAnswers(null);
    setQuestionIndex(0);
  };

  const openMode = (nextMode) => {
    setMode(nextMode);
    setSelectedSubject("");
    setSelectedPaper("");
    setSessionStarted(false);
    setSummaryAnswers(null);
    setQuestionIndex(0);
    aibeNavigation?.pushAibeState({
      screen: "practice",
      view: nextMode === "random" ? "ready" : "selection",
      mode: nextMode,
    });
  };

  const returnToSetup = () => {
    if (mode === "random") {
      resetSession();
      aibeNavigation?.backAibe({ screen: "practice", view: "setup" });
      return;
    }
    setSessionStarted(false);
    setQuestionIndex(0);
    aibeNavigation?.backAibe({
      screen: "practice",
      view: "selection",
      mode,
    });
  };

  const selectPracticeItem = (item) => {
    if (mode === "subject") {
      setSelectedSubject(item.id);
      setSelectedPaper("");
    } else {
      setSelectedPaper(item.id);
      setSelectedSubject("");
    }

    setSessionStarted(false);
    setSummaryAnswers(null);
    setQuestionIndex(0);
    aibeNavigation?.pushAibeState(
      buildPracticeState({
        view: "ready",
        subjectId: mode === "subject" ? item.id : "",
        paperId: mode === "paper" ? item.id : "",
        questionIndex: 0,
      })
    );
  };

  const startPracticeSession = () => {
    setSessionStarted(true);
    setSummaryAnswers(null);
    setQuestionIndex(0);
    aibeNavigation?.pushAibeState(buildPracticeState({
      view: "session",
      questionIndex: 0,
    }));
  };

  const openSummary = (answers) => {
    setSummaryAnswers(answers);
    aibeNavigation?.pushAibeState(buildPracticeState({
      view: "summary",
      questionIndex,
    }));
  };

  const backFromSummary = () => {
    setSummaryAnswers(null);
    aibeNavigation?.backAibe(buildPracticeState({
      view: "session",
      questionIndex,
    }));
  };

  const backToPracticeOptions = () => {
    resetSession();
    aibeNavigation?.backAibe({ screen: "practice", view: "setup" });
  };

  const handleQuestionNavigate = (nextIndex) => {
    setQuestionIndex(nextIndex);
    aibeNavigation?.pushAibeState(buildPracticeState({
      view: "session",
      questionIndex: nextIndex,
    }));
  };

  const handleQuestionChange = useCallback((question, index) => {
    setQuestionIndex(index);
  }, []);

  if (summaryAnswers) {
    return (
      <SummaryPage
        answers={summaryAnswers}
        total={questions.length || summaryAnswers.length}
        onBack={backFromSummary}
        onAgain={backToPracticeOptions}
        onPracticePage={() => { window.location.href = "/aibe-preparation/practice"; }}
      />
    );
  }

  return (
    <section className="aibe-practice-centre" id="practice-centre">
      {!mode && (
        <h2 className="aibe-block-heading">
          Choose your Option...
        </h2>
      )}
      {!mode && <PracticeCards onOpen={openMode} />}

      {mode === "subject" && !selectedSubject && !sessionStarted && (
        <div className="aibe-practice-selection-section">

          <h2 className="aibe-block-heading">
            Subject Wise
          </h2>

          <PracticeSelectionGrid
            items={subjectItems}
            type="subject"
            onSelect={selectPracticeItem}
          />

        </div>
      )}
      {mode === "paper" && !selectedPaper && !sessionStarted && (
        <div className="aibe-practice-selection-section">

          <h2 className="aibe-block-heading">
            Previous Paper Wise
          </h2>

          <PracticeSelectionGrid
            items={paperItems}
            type="paper"
            onSelect={selectPracticeItem}
          />

        </div>
      )}
      {mode && questions.length > 0 && !sessionStarted && (
        <section className="aibe-practice-tip-card">
          <h3>Practice Tip</h3>
          <p>
            Before starting this practice session, keep the relevant physical Bare Act nearby and use it without notes. Practising with the actual book helps you locate provisions quickly, build familiarity and speed, and simulate real AIBE conditions.
          </p>
          <div className="aibe-action-row">
            <button type="button" className="aibe-btn-secondary" onClick={returnToSetup}>
              {mode === "random" ? "Back to Options" : "Change Selection"}
            </button>
            <button type="button" className="aibe-btn-primary" onClick={startPracticeSession}>
              Start Practice
            </button>
          </div>
        </section>
      )}

      {mode && sessionStarted && (
        <div className="aibe-practice-workspace">
          <QuestionInterface
            questions={questions}
            initialIndex={activeQuestionIndex}
            mode={practiceModeLabel}
            onExit={openSummary}
            onQuestionChange={handleQuestionChange}
            onQuestionNavigate={handleQuestionNavigate}
          />
        </div>
      )}
    </section>
  );
}
